import React, { useState } from 'react';
import { format } from 'date-fns';
import { hrService } from '../../../lib/checkInAttendanceService';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LeaveApproval = ({ leaveRequests, loading, onRefresh, managerId }) => {
  const [filterStatus, setFilterStatus] = React.useState('pending');

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'bg-success/10 text-success';
      case 'rejected':
        return 'bg-error/10 text-error';
      case 'pending':
        return 'bg-warning/10 text-warning';
      case 'cancelled':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const handleApprove = async (leaveId) => {
    const { error } = await hrService?.updateLeaveRequest(leaveId, {
      status: 'approved',
      reviewedBy: managerId
    });
    if (!error) {
      onRefresh?.();
    }
  };

  const handleReject = async (leaveId, notes) => {
    const { error } = await hrService?.updateLeaveRequest(leaveId, {
      status: 'rejected',
      reviewedBy: managerId,
      reviewNotes: notes || 'Request rejected'
    });
    if (!error) {
      onRefresh?.();
    }
  };

  const filteredRequests = leaveRequests?.filter((request) => {
    return filterStatus === 'all' || request?.status === filterStatus;
  });

  const pendingCount = leaveRequests?.filter((r) => r?.status === 'pending')?.length || 0;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="card">
        <div className="flex items-center gap-4">
          <div className="p-4 rounded-lg bg-warning/10">
            <Icon name="Clock" size={32} className="text-warning" />
          </div>
          <div>
            <div className="text-3xl font-bold text-foreground">{pendingCount}</div>
            <div className="text-sm text-muted-foreground">Pending Leave Requests</div>
          </div>
        </div>
      </div>
      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold text-foreground">Leave Requests</h2>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e?.target?.value)}
              className="px-4 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <Button onClick={onRefresh} variant="outline" disabled={loading}>
            <Icon name={loading ? 'Loader2' : 'RefreshCw'} size={18} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>
      {/* Leave Requests */}
      {loading && leaveRequests?.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : filteredRequests?.length === 0 ? (
        <div className="card text-center py-12">
          <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Leave Requests</h3>
          <p className="text-muted-foreground">Leave requests will appear here</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequests?.map((request) => (
            <div key={request?.id} className="card">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">
                      {request?.staff?.full_name}
                    </h3>
                    <p className="text-sm text-muted-foreground capitalize">
                      {request?.leave_type?.replace('_', ' ')} Leave
                    </p>
                    <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(request?.status)}`}>
                      {request?.status}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-primary">{request?.total_days}</div>
                  <div className="text-xs text-muted-foreground">days</div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 p-4 bg-muted/30 rounded-lg">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Start Date</div>
                  <div className="font-semibold text-foreground">
                    {format(new Date(request?.start_date), 'MMM d, yyyy')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">End Date</div>
                  <div className="font-semibold text-foreground">
                    {format(new Date(request?.end_date), 'MMM d, yyyy')}
                  </div>
                </div>
              </div>

              {request?.reason && (
                <div className="mb-4 p-4 bg-muted/30 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Reason</div>
                  <p className="text-sm text-foreground">{request?.reason}</p>
                </div>
              )}

              {request?.review_notes && (
                <div className="mb-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="text-xs text-muted-foreground mb-1">Review Notes</div>
                  <p className="text-sm text-foreground">{request?.review_notes}</p>
                </div>
              )}

              {request?.status === 'pending' && (
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => handleApprove(request?.id)}
                    size="sm"
                  >
                    <Icon name="Check" size={16} />
                    <span>Approve</span>
                  </Button>
                  <Button
                    onClick={() => handleReject(request?.id)}
                    variant="destructive"
                    size="sm"
                  >
                    <Icon name="X" size={16} />
                    <span>Reject</span>
                  </Button>
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                <span>Requested: {format(new Date(request?.created_at), 'MMM d, yyyy')}</span>
                {request?.reviewed_at && (
                  <span>Reviewed: {format(new Date(request?.reviewed_at), 'MMM d, yyyy')}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeaveApproval;